import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Modal,
  Backdrop,
  Fade,
  Divider,
  List,
  ListItem
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TopNav from './Navbar';
import LeftNavbar from './LeftNav';
import Breadcrumb from './BreadCrump';
import FaceRecognition from './FaceRecognition'; // ✅ Import the component

const AutomaticExamTwo = () => {
  const location = useLocation();
  const examDetails = location.state?.exam;
  const [openModal, setOpenModal] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const examId = examDetails?.id;
  const API_BASE = 'http://196.216.9.34:5020/api/exams';
  const token = localStorage.getItem('token');
  const [timeLeft, setTimeLeft] = useState(0);
  const [faceVerified, setFaceVerified] = useState(false); // ✅ Track verification

  const parseDurationToSeconds = (hhmm) => {
    const [hours, minutes] = hhmm.split(':').map(Number);
    return (hours * 60 + minutes) * 60;
  };

  useEffect(() => {
    if (examDetails?.duration && openModal) {
      const totalSeconds = parseDurationToSeconds(examDetails.duration);
      setTimeLeft(totalSeconds);

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [examDetails?.duration, openModal]);

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const handleClose = () => setOpenModal(false);

  const handleSubmitAnswers = async () => {
    if (!examDetails || !Object.keys(userAnswers).length) {
      alert("Please answer at least one question before submitting.");
      return;
    }
  
    if (!faceVerified) {
      alert("Face verification required before submitting.");
      return;
    }
  
    try {
      const payload = {
        answers: Object.entries(userAnswers).map(([question_id, student_answer]) => ({
          question_id: parseInt(question_id),
          student_answer
        }))
      };
  
      // ✅ 1. Submit student answers
      const response = await fetch(`${API_BASE}/${examDetails.id}/attempts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) throw new Error('Failed to submit answers');
      alert("✅ Answers submitted successfully! Exam will now be marked automatically.");
  
      // ✅ 2. Get current user (student_id) from localStorage or token payload
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const studentId = storedUser?.id;
      if (!studentId) {
        alert("Student ID not found. Please log in again.");
        return;
      }
  
      // ✅ 3. Call AI-based marking API (your Python backend)
      const MARKING_API = `http://196.216.9.34:5021/api/mark_exam?student_id=${studentId}&exam_id=${examDetails.id}`;
      const markResponse = await fetch(MARKING_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      if (!markResponse.ok) {
        const errorText = await markResponse.text();
        throw new Error(`Exam marking failed: ${errorText}`);
      }
  
      const result = await markResponse.json();
      console.log("Marking result:", result);
  
      alert("🎓 Exam marked successfully! Check your results or certificate page.");
  
      handleClose();
    } catch (error) {
      console.error(error);
      alert("An error occurred during submission or marking.");
    }
  };
  

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!examId) return;
      try {
        const res = await fetch(`${API_BASE}/${examId}/questions`);
        if (!res.ok) throw new Error('Failed to fetch questions');
        const data = await res.json();
        setQuestions(data);
      } catch (err) {
        console.error('Error fetching questions:', err.message);
      }
    };

    if (openModal && examDetails) {
      fetchQuestions();
    }
  }, [openModal, examDetails, examId]);

  return (
    <Box sx={{ display: 'flex' }}>
      <LeftNavbar />
      <Box sx={{ flexGrow: 1, ml: { lg: '240px' } }}>
        <TopNav />
        <Box sx={{ p: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          <Breadcrumb currentPage="Automatic Exam" />
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 4, color: '#ff6600' }}>
            <CameraAltIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Automatic Face Verification
          </Typography>

          <Card sx={{ p: 4, boxShadow: 3 }}>
            <CardContent>
              {/* ✅ Replace manual matching with this component */}
              <FaceRecognition
                examDetails={examDetails}
                onVerificationSuccess={() => {
                  setFaceVerified(true);
                  setOpenModal(true);
                }}
                
              />

              {faceVerified && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  ✅ Face verified! You may now proceed with the exam.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ✅ Exam Questions Modal */}
      <Modal
        open={openModal}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="#ff6600" gutterBottom>
              📘 Exam Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: '2px dashed #1976d2',
                borderRadius: 2,
                backgroundColor: '#e3f2fd',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                ⏳ Time Remaining: {formatTime(timeLeft)}
              </Typography>
            </Box>

            {examDetails ? (
              <>
                <Box mb={2}>
                  <Typography><strong>Title:</strong> {examDetails.title}</Typography>
                  <Typography><strong>Subject:</strong> {examDetails.subject}</Typography>
                  <Typography><strong>Duration:</strong> {examDetails.duration}</Typography>
                  <Typography><strong>Scheduled At:</strong> {new Date(examDetails.scheduledAt).toLocaleString()}</Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" color="primary" gutterBottom>
                  ✏️ Questions
                </Typography>

                {questions.length > 0 ? (
                  <List sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {questions.map((q, index) => (
                      <Box
                        key={q.id}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid #e0e0e0',
                          p: 3,
                          backgroundColor: '#fafafa',
                          boxShadow: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Q{index + 1}: {q.question}
                        </Typography>

                        {q.type.trim().toLowerCase() === 'multiple choice' && q.options ? (
                          (() => {
                            let optionsArray = [];

                            if (typeof q.options === 'string') {
                              try {
                                const parsed = JSON.parse(q.options);
                                optionsArray = Array.isArray(parsed) ? parsed : q.options.split(',');
                              } catch {
                                optionsArray = q.options.split(',');
                              }
                            } else if (Array.isArray(q.options)) {
                              optionsArray = q.options;
                            }

                            return optionsArray.map((option, i) => (
                              <Box key={i} sx={{ mb: 1 }}>
                                <label style={{ display: 'flex', alignItems: 'center' }}>
                                  <input
                                    type="radio"
                                    name={`question_${q.id}`}
                                    value={option}
                                    checked={userAnswers[q.id] === option}
                                    onChange={(e) =>
                                      setUserAnswers((prev) => ({
                                        ...prev,
                                        [q.id]: e.target.value,
                                      }))
                                    }
                                    style={{ marginRight: 8 }}
                                  />
                                  <Typography variant="body2">{option}</Typography>
                                </label>
                              </Box>
                            ));
                          })()
                        ) : (
                          <textarea
                            name={`question_${q.id}`}
                            rows={4}
                            placeholder="Write your answer here..."
                            style={{
                              width: '100%',
                              borderRadius: '8px',
                              padding: '12px',
                              border: '1px solid #ccc',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                              resize: 'vertical',
                            }}
                            value={userAnswers[q.id] || ''}
                            onChange={(e) =>
                              setUserAnswers((prev) => ({
                                ...prev,
                                [q.id]: e.target.value,
                              }))
                            }
                          />
                        )}
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2">No questions available.</Typography>
                )}

                <Box textAlign="right" mt={4}>
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    onClick={handleSubmitAnswers}
                    sx={{ borderRadius: '8px', px: 4 }}
                    disabled={!faceVerified} // ✅ disable if face not verified
                  >
                    Submit Answers
                  </Button>
                </Box>
              </>
            ) : (
              <Typography>No exam data available.</Typography>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default AutomaticExamTwo;
